import React, { useEffect, useState } from 'react';
import back from '../dist/webImages/back.svg';
import { Field, Form, Formik } from 'formik';
import { Link, useNavigate, useParams } from 'react-router-dom';
import swal from 'sweetalert';
import { toast } from 'react-toastify';
import FormControl from '../components/form/FormControl';
import SubmitButton from '../components/SubmitButton';
import SkeletonCustomerCreateEdit from './SkeletonCustomerCreateEdit';
import OneImageUpload from '../components/OneImageUpload';
import useGet from '../customHooks/useGet';
import usePost2 from '../customHooks/usePost2';

const canAccessCustomerAction = (permission, action) =>
  permission?.Customers?.includes(action) ||
  permission?.Customer?.includes(action);

const CustomersEdit = ({ permission }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [resget, apiMethodGet] = useGet();
  const [res, apiMethod] = usePost2();
  const [datas, setDatas] = useState({
    profile_image: '',
  });
  const [imageLoader, setImageLoader] = useState(false);

  useEffect(() => {
    apiMethodGet('customers/' + id);
  }, [id]);

  useEffect(() => {
    if (res.error) {
      toast.error(res?.error?.response?.data?.message ?? 'Failed to update customer');
    }
  }, [res.error]);

  useEffect(() => {
    if (res.data) {
      const { status, message } = res.data;

      if (status === 'false' || status === false) {
        toast.error(message);
        return;
      }

      toast.success(message ?? 'Customer updated successfully');
      navigate('/customers');
    }
  }, [navigate, res.data]);

  const customerData = resget?.data?.data ?? {};

  useEffect(() => {
    if (customerData && Object.keys(customerData).length > 0) {
      const imageValue = customerData?.profile_image_path || customerData?.profile_image || '';
      setDatas({
        profile_image: imageValue,
        profile_image_value: imageValue,
      });
    }
  }, [customerData]);

  const initialValues = {
    name: customerData?.name ?? '',
    email: customerData?.email ?? '',
    phone: customerData?.phone ?? '',
    password: '',
    profile_image: customerData?.profile_image_path ?? customerData?.profile_image ?? '',
    fcm_token: customerData?.fcm_token ?? '',
    is_active: String(customerData?.is_active ?? 1),
  };

  const handleSubmit = async (values) => {
    if (imageLoader) {
      swal({
        title: 'Wait a few seconds',
        text: 'Image is still uploading.',
        icon: 'error',
        dangerMode: true,
      });
      return;
    }

    const requiredFields = ['name', 'email', 'phone'];
    const fieldLabels = {
      name: 'Name',
      email: 'Email',
      phone: 'Phone',
    };

    const emptyFields = requiredFields
      .filter((field) => !values[field]?.trim())
      .map((field) => fieldLabels[field]);

    if (emptyFields.length > 0) {
      swal({
        title: 'Required fields are empty',
        text: emptyFields.join(', '),
        icon: 'error',
        dangerMode: true,
      });
      return;
    }

    const finalProfileImage = datas?.profile_image_value || values.profile_image?.trim() || '';
    const payload = {
      name: values.name.trim(),
      email: values.email.trim(),
      phone: values.phone.trim(),
      profile_image: finalProfileImage,
      is_active: String(values.is_active) === '1' ? 1 : 0,
      fcm_token: values.fcm_token?.trim() || '',
    };

    if (values.password?.trim()) {
      payload.password = values.password.trim();
    }

    await apiMethod('customers/' + id, payload);
  };

  if (resget.isLoading || !resget.data) {
    return <SkeletonCustomerCreateEdit heading='Edit Customer' />;
  }

  return (
    <section className='PushNotificationCreate pr-10 max-lg:pr-6'>
      <Link to='/customers' className='back flex items-center mb-5 gap-2'>
        <img src={back} className='w-[2rem]' alt='' />
        <span className='text-[1.4rem] font-MluvkaBold'>Edit Customer</span>
      </Link>

      <div className='relative flex items-start gap-3'>
        <div className='bg-white rounded-xl w-full mx-auto relative'>
          <Formik initialValues={initialValues} onSubmit={handleSubmit} enableReinitialize>
            <Form name='customersEditForm'>
              <div className='bg-[#EFF4FD] p-6 rounded-3xl mb-3 max-lg:p-3'>
                <div className='grid grid-cols-2 gap-4 max-lg:grid-cols-1'>
                  <FormControl
                    name='name'
                    label='Name'
                    placeholder='Enter name'
                    className='outline-none w-full h-[3rem] px-5 rounded-xl'
                    control='input2'
                  />
                  <FormControl
                    name='email'
                    label='Email'
                    placeholder='Enter email'
                    className='outline-none w-full h-[3rem] px-5 rounded-xl'
                    control='input2'
                  />
                </div>

                <div className='grid grid-cols-2 gap-4 mt-4 max-lg:grid-cols-1'>
                  <FormControl
                    name='phone'
                    label='Phone'
                    placeholder='Enter phone'
                    className='outline-none w-full h-[3rem] px-5 rounded-xl'
                    control='input2'
                  />
                  <FormControl
                    name='password'
                    label='Password'
                    placeholder='Leave blank to keep current password'
                    className='outline-none w-full h-[3rem] px-5 rounded-xl'
                    control='password'
                  />
                </div>

                <div className='grid grid-cols-2 gap-4 mt-4 max-lg:grid-cols-1'>
                  <FormControl
                    name='fcm_token'
                    label='FCM Token'
                    placeholder='Enter FCM token'
                    className='outline-none w-full h-[3rem] px-5 rounded-xl'
                    control='input2'
                  />

                  <div className='inputBox w-full mt-3'>
                    <label className='mb-2 block text-[#7D8CA7] text-[.8rem]'>
                      Status
                    </label>
                    <Field
                      as='select'
                      name='is_active'
                      className='outline-none w-full h-[3rem] px-5 rounded-xl'
                    >
                      <option value='1'>Active</option>
                      <option value='0'>Inactive</option>
                    </Field>
                  </div>
                </div>

                <div className='mt-4'>
                  <FormControl
                    name='profile_image'
                    label='Profile Image Path'
                    placeholder='Paste image path if needed'
                    className='outline-none w-full h-[3rem] px-5 rounded-xl'
                    control='input2'
                  />
                </div>

                <div className='mt-4'>
                  <label className='mb-2 block text-[#7D8CA7] text-[.8rem]'>
                    Upload Image
                  </label>
                  <OneImageUpload
                    changeImage={setImageLoader}
                    MainImage={datas?.profile_image}
                    Update={setDatas}
                    sec_value={'profile_image_value'}
                    sec_image={'profile_image'}
                    folder_name={'profile-image'}
                    page_type={'customer'}
                  />
                </div>
              </div>

              {canAccessCustomerAction(permission, 'Customer Edit') && (
                <SubmitButton
                  props={{
                    class: 'btn bg-secondary text-white px-12 ml-auto uppercase mb-3 py-3 rounded-full w-100 block mt-5 submit hover:bg-primary transition-all duration-300',
                    text: 'Update',
                  }}
                  buttonLoading={res.isLoading}
                />
              )}
            </Form>
          </Formik>
        </div>
      </div>
    </section>
  );
};

export default CustomersEdit;
